import {
  ChecklistRounded,
  ChevronRightRounded,
  DriveFileRenameOutlineRounded,
  StyleRounded,
} from "@mui/icons-material";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { Page, PageHeader } from "../../../components/layout/Page";
import SegmentedControl from "../../../components/ui/SegmentedControl";
import { ReactComponent as FlameIcon } from "../../../img/iconImgAllFlame.svg";
import Term from "../../term/Term";
import styles from "../css/LearningSelectScreen.module.css";
import {
  deleteLearningContinue,
  fetchLearningHistory as fetchLearningHistoryFromAPI,
  fetchMyStreakFlames,
} from "../util/learningAPI";
import { fetchLearningContinue as fetchLearningContinueFromAPI } from "../util/learningAPI";
import { LearningType } from "../model/LearningType";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import LearningOptions from "./LearningOptions";
import Group from "../../groups/Group";
import { updateLearningDailyTargetCount as updateLearningDailyTargetCountToServer } from "../util/learningAPI";
import { TermLevel } from "../../term/custom_view/TermLevel";
import { SortType } from "../../settings/model/SortType";
import { getFilteredTermsWithOptions } from "../../term/terms";
import {
  getLearningSelectedGroupsFromLocalStorage,
  getLearningSelectedLevelFromLocalStroage,
  setLearningSelectedGroupsToLocalStroage,
  setLearningSelectedLevelsToLocalStroage,
  setLearningSortTypeToLocalStorage,
} from "../../settings/util/learningSettings";
import { useTerms } from "../../term/useTerms";
import { LearningMode } from "../model/LearningMode";
import { fetchLearningHistoryTerms, fetchMyFlames } from "../util/learningAPI";
import { LearningHistoryRange } from "../model/learningHistoryRange";
import LearningHistoryTermsResult from "../model/LearningHistoryTerms";
import { getDailyTargetMessage } from "../util/learning";
import CountPicker from "../../common/CountPicker";
import { CountPickerStyle } from "../../common/CountPickerStyle";
import LearningHistoryScreen from "./LearningHistoryScreen";
import LearningHistory from "../model/LearningHistory";
import LearnedTermsScreen from "./LearnedTermsScreen";
import LearningRetry from "../model/LearningRetry";
import LearningContinueResult from "../model/LearningContinueResult";
import { toDecimalString } from "../../common/strings";
import LoadingScreen from "../../common/LoadingScreen";

export default function LearningSelectScreen() {
  const navigate = useNavigate();

  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isStreakCountLoading, setIsStreakCountLoading] =
    useState<boolean>(false);
  const [isCumulativeCountLoading, setIsCumulativeCountLoading] =
    useState<boolean>(false);

  const [terms] = useTerms();
  const [selectedTab, setSelectedTab] = useState<number>(1);

  const [flameCount, setFlameCount] = useState<number>(0);
  const [streakCount, setStreakCount] = useState<number>(0);

  const [isGoal, setIsGoal] = useState<Boolean>(false);

  const [learningTermsResult, setLearningTermsResult] =
    useState<LearningHistoryTermsResult>();
  const [learningHistories, setLearningHistories] = useState<LearningHistory[]>(
    [
      LearningHistory.getEmpty(),
      LearningHistory.getEmpty(),
      LearningHistory.getEmpty(),
      LearningHistory.getEmpty(),
    ]
  );
  const [learningContinueResult, setLearningContinueResult] =
    useState<LearningContinueResult | null>(null);

  const [filteredTerms, setFilteredTerms] = useState<Term[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<Group[]>([]);
  const [selectedLevels, setSelectedLevels] = useState<TermLevel[]>([]);
  const [sortType, setSortType] = useState(SortType.random);

  const [
    openLearningDailyTargetCountUpdate,
    setOpenLearningDailyTargetCountUpdate,
  ] = useState<boolean>(false);

  const [openLearningSettings, setOpenLearningSettings] =
    useState<LearningType | null>(null);

  const [showingLeanredTerms, setShowingLearnedTerms] =
    useState<LearningHistory | null>(null);

  useEffect(() => {}, [showingLeanredTerms]);

  useEffect(() => {
    setSelectedGroups(getLearningSelectedGroupsFromLocalStorage());
    setSelectedLevels(getLearningSelectedLevelFromLocalStroage());
    fetchFlameCountFromServer();
    fetchStreakCountFromServer();
    fetchLearningHistoryFromServer();
    fetchLearningHistory();
    fetchLearningContinue();
  }, []);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;

    if (showingLeanredTerms) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [showingLeanredTerms]);

  useEffect(() => {
    const correctTermsCount = learningTermsResult?.correctTermsCount;
    const goalCount = learningTermsResult?.goalCount;
    if (correctTermsCount !== undefined && goalCount !== undefined) {
      setIsGoal(correctTermsCount >= goalCount);
    }
  }, [learningTermsResult]);

  useEffect(() => {
    updateFilteredList();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroups, selectedLevels, sortType]);

  function updateFilteredList() {
    setFilteredTerms(
      getFilteredTermsWithOptions(
        terms,
        undefined,
        sortType,
        selectedGroups,
        selectedLevels
      )
    );
  }

  function showUpdateLearningDailyTargetCountScreen() {
    setOpenLearningDailyTargetCountUpdate(true);
  }

  function handleOnClickLearningType(type: LearningType) {
    setOpenLearningSettings(type);
  }

  function startLearning(learningType: LearningType) {
    navigate("/learning", {
      state: {
        defaultTerms: null,
        learningType: learningType,
        learningMode: LearningMode.normal,
        learningContinue: null,
      },
    });
    deleteLearningContinue();
  }

  function continueLearning(
    learningContinueResult: LearningContinueResult,
    learningType: LearningType
  ) {
    navigate("/learning", {
      state: {
        defaultTerms: learningContinueResult.terms,
        learningType: learningType,
        learningMode: LearningMode.normal,
        learningContinue: learningContinueResult,
      },
    });
  }

  function reviewLearning(item: LearningRetry) {
    navigate("/learning", {
      state: {
        defaultTerms: item.terms,
        learningType: item.type,
        learningMode: LearningMode.restartFromLearningHistory,
        learningContinue: null,
      },
    });
  }

  async function fetchFlameCountFromServer() {
    setIsCumulativeCountLoading(true);
    const newFlameCount = await fetchMyFlames();
    setFlameCount(newFlameCount);
    setIsCumulativeCountLoading(false);
  }

  async function fetchStreakCountFromServer() {
    setIsStreakCountLoading(true);
    const newCount = await fetchMyStreakFlames();
    setStreakCount(newCount);
    setIsStreakCountLoading(false);
  }

  async function fetchLearningHistoryFromServer() {
    const learningHistoryTerms = await fetchLearningHistoryTerms(
      LearningHistoryRange.day
    );
    setLearningTermsResult(learningHistoryTerms);
  }

  async function updateLearningDailyTargetCount(count: number) {
    await updateLearningDailyTargetCountToServer(count);
    fetchLearningHistoryFromServer();
    fetchFlameCountFromServer();
    fetchStreakCountFromServer();
  }

  async function fetchLearningContinue() {
    setIsLoading(true);
    try {
      const result = await fetchLearningContinueFromAPI();
      setLearningContinueResult(result);
    } catch (error) {
      // 이어하기 정보는 없어도 학습을 시작할 수 있으므로 화면을 막지 않는다
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }

  async function fetchLearningHistory() {
    const newLearningHistories: LearningHistory[] = [];
    newLearningHistories.push(
      await fetchLearningHistoryFromAPI(LearningHistoryRange.day)
    );
    newLearningHistories.push(
      await fetchLearningHistoryFromAPI(LearningHistoryRange.yesterday)
    );
    newLearningHistories.push(
      await fetchLearningHistoryFromAPI(LearningHistoryRange.three_days)
    );
    newLearningHistories.push(
      await fetchLearningHistoryFromAPI(LearningHistoryRange.week)
    );
    newLearningHistories.push(
      await fetchLearningHistoryFromAPI(LearningHistoryRange.month)
    );
    newLearningHistories.push(
      await fetchLearningHistoryFromAPI(LearningHistoryRange.all)
    );
    setLearningHistories(newLearningHistories);
  }

  function handleOnClickLearningHistoryItem(item: LearningHistory) {
    setShowingLearnedTerms(item);
  }

  const correctCount = learningTermsResult?.correctTermsCount ?? 0;
  const goalCount = learningTermsResult?.goalCount ?? 0;
  const goalProgress =
    goalCount > 0 ? Math.min(100, (correctCount / goalCount) * 100) : 0;
  const hasLearningContinue =
    learningContinueResult !== null && learningContinueResult.terms.length > 0;

  const learningModes = [
    {
      type: LearningType.flashcards,
      title: t("Flashcard"),
      description: t("FlashcardsExplain"),
      icon: <StyleRounded />,
      tone: styles.toneBrand,
    },
    {
      type: LearningType.multiplechoice,
      title: t("MultipleChoiceTitle"),
      description: t("MultipleChoiceExplain"),
      icon: <ChecklistRounded />,
      tone: styles.toneGreen,
    },
    {
      type: LearningType.spellingQuiz,
      title: t("SpellingQuiz"),
      description: t("SpellQuizExplain"),
      icon: <DriveFileRenameOutlineRounded />,
      tone: styles.tonePink,
    },
  ];

  return (
    <>
      <Page>
        <PageHeader
          title={t("LearningTitle")}
          actions={
            <SegmentedControl
              aria-label={t("LearningTitle")}
              size="sm"
              fullWidth={false}
              value={selectedTab === 1 ? "learn" : "history"}
              onChange={(value) => setSelectedTab(value === "learn" ? 1 : 2)}
              options={[
                { value: "learn", label: t("LearningTabBarItemLearning") },
                { value: "history", label: t("LearningTabBarItemHistory") },
              ]}
            />
          }
        />

        {selectedTab === 1 && (
          <>
            {hasLearningContinue && (
              <div className={styles.continueBanner} role="status">
                {t("LearningContinueMessage")}
              </div>
            )}

            <button
              type="button"
              className={styles.goalCard}
              onClick={showUpdateLearningDailyTargetCountScreen}
              aria-label={t("LearningTodayGoalSettingDialogTitle")}
            >
              <div className={styles.goalTop}>
                <div className={styles.goalText}>
                  <span className={styles.goalLabel}>
                    {t("LearningTodayGoalCardTitle")}
                  </span>
                  <span className={styles.goalValue}>
                    {toDecimalString(correctCount)}
                    <span className={styles.goalTotal}>
                      {" "}
                      / {toDecimalString(goalCount)}
                    </span>
                  </span>
                </div>
                <span
                  className={classNames(styles.flameTile, {
                    [styles.flameTileLit]: isGoal,
                  })}
                  aria-hidden
                >
                  <FlameIcon />
                </span>
              </div>
              <div className={styles.progress} aria-hidden>
                <div
                  className={classNames(styles.progressBar, {
                    [styles.progressDone]: isGoal,
                  })}
                  style={{ width: `${goalProgress}%` }}
                />
              </div>
              <div className={styles.goalBottom}>
                <span className={styles.goalMessage}>
                  {getDailyTargetMessage(learningTermsResult)}
                </span>
                <span className={styles.flameStats}>
                  <span>
                    {t("LearningGoalStreak")}{" "}
                    <strong>
                      {isStreakCountLoading
                        ? "–"
                        : toDecimalString(streakCount)}
                    </strong>
                  </span>
                  <span>
                    {t("LearningGoalCumulative")}{" "}
                    <strong>
                      {isCumulativeCountLoading
                        ? "–"
                        : toDecimalString(flameCount)}
                    </strong>
                  </span>
                </span>
              </div>
            </button>

            <div className={styles.modes}>
              {learningModes.map((mode) => (
                <button
                  key={mode.type}
                  type="button"
                  className={styles.mode}
                  onClick={() => handleOnClickLearningType(mode.type)}
                >
                  <span className={classNames(styles.modeIcon, mode.tone)}>
                    {mode.icon}
                  </span>
                  <span className={styles.modeText}>
                    <span className={styles.modeTitle}>{mode.title}</span>
                    <span className={styles.modeDescription}>
                      {mode.description}
                    </span>
                  </span>
                  <ChevronRightRounded className={styles.chevron} />
                </button>
              ))}
            </div>
          </>
        )}
        {selectedTab === 2 && (
          <LearningHistoryScreen
            items={learningHistories}
            onClickItem={handleOnClickLearningHistoryItem}
            onStartReview={reviewLearning}
          />
        )}
      </Page>

      <LearnedTermsScreen
        open={showingLeanredTerms !== null}
        history={showingLeanredTerms}
        onRetry={(item: LearningRetry) => {
          setShowingLearnedTerms(null);
          reviewLearning(item);
        }}
        onCancel={() => {
          setShowingLearnedTerms(null);
        }}
      />

      <CountPicker
        open={openLearningDailyTargetCountUpdate === true}
        defaultValue={learningTermsResult?.goalCount ?? 30}
        style={CountPickerStyle.learningGoalCount}
        onComplete={(count: number) => updateLearningDailyTargetCount(count)}
        onClose={() => {
          setOpenLearningDailyTargetCountUpdate(false);
        }}
      />

      <LearningOptions
        open={openLearningSettings !== null}
        terms={filteredTerms}
        type={openLearningSettings}
        learningContinue={learningContinueResult}
        mode="beforeLearning"
        onUpdateSelectedGroups={(groups: Group[]) => {
          setLearningSelectedGroupsToLocalStroage(groups);
          setSelectedGroups(groups);
        }}
        onUpdateSelectedLevels={(levels: TermLevel[]) => {
          setLearningSelectedLevelsToLocalStroage(levels);
          setSelectedLevels(levels);
        }}
        onUpdateSortType={(sortType: SortType) => {
          setLearningSortTypeToLocalStorage(sortType);
          setSortType(sortType);
        }}
        onUpdateShowingPronunciationSelectView={() => {}}
        onClickStartButton={startLearning}
        onClickContinueButton={(
          learningType: LearningType,
          learningContinue: LearningContinueResult
        ) => {
          continueLearning(learningContinue, learningType);
        }}
        onClose={() => {
          document.body.style.overflow = "auto";
          setOpenLearningSettings(null);
        }}
      />

      {isLoading === true ? <LoadingScreen /> : null}
    </>
  );
}
